import React, { useState } from 'react';
import { User, Mail, Lock, LogIn, UserPlus, ChevronRight, Info, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

const Profile = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
    const [formData, setFormData] = useState({ email: '', password: '', full_name: '', city: '', zipcode: '' });
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (isLogin) {
                const data = await api.login({ email: formData.email, password: formData.password });
                setUser(data.user);
            } else {
                await api.register(formData);
                setIsLogin(true); // Switch to login after registration
                setError('Registration successful! Please log in.');
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleLogout = () => {
        api.logout();
        setUser(null);
    };

    return (
        <div className="p-4 max-w-2xl mx-auto">
            <header className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
                <p className="text-gray-500">Manage your account and preferences</p>
            </header>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <div className="p-6">
                    <div className="flex flex-col items-center mb-6">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <User size={40} className="text-gray-400" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900">{user ? user.full_name : 'Guest User'}</h2>
                        <p className="text-sm text-gray-500">{user ? user.email : 'Sign in to access more features'}</p>
                        {user && (
                            <button onClick={handleLogout} className="mt-2 text-sm text-red-600 hover:underline">
                                Sign Out
                            </button>
                        )}
                    </div>

                    {!user && (
                        <div className="space-y-4">
                            {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>}

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                {!isLogin && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                            <div className="relative">
                                                <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    name="full_name"
                                                    value={formData.full_name}
                                                    onChange={handleChange}
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                                    placeholder="John Doe"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="New York"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Zipcode</label>
                                                <input
                                                    type="text"
                                                    name="zipcode"
                                                    value={formData.zipcode}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="10001"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <div className="relative">
                                        <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            placeholder="you@example.com"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            placeholder="••••••••"
                                            required
                                        />
                                    </div>
                                </div>
                                <button className={`w-full py-2 ${isLogin ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold rounded-lg transition-colors flex items-center justify-center`}>
                                    {isLogin ? <><LogIn size={18} className="mr-2" /> Sign In</> : <><UserPlus size={18} className="mr-2" /> Create Account</>}
                                </button>

                                <div className="relative my-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={async () => {
                                        // Mock Google Login for Demo
                                        try {
                                            const data = await api.googleLogin('mock-google-token');
                                            setUser(data.user);
                                        } catch (err) {
                                            setError('Google Login Failed');
                                        }
                                    }}
                                    className="w-full py-2 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                                >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Sign in with Google
                                </button>
                            </form>

                            <div className="text-center mt-4">
                                <button
                                    onClick={() => setIsLogin(!isLogin)}
                                    className="text-sm text-blue-600 hover:underline font-medium"
                                >
                                    {isLogin ? "Don't have an account? Register" : "Already have an account? Sign In"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                {user && user.role === 'admin' && (
                    <button
                        onClick={() => navigate('/admin')}
                        className="w-full flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100"
                    >
                        <div className="flex items-center">
                            <Shield size={20} className="text-blue-600 mr-3" />
                            <span className="text-blue-900 font-bold">Admin Dashboard</span>
                        </div>
                        <ChevronRight size={20} className="text-blue-400" />
                    </button>
                )}
                <button className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50">
                    <span className="text-gray-700 font-medium">Notifications</span>
                    <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50">
                    <span className="text-gray-700 font-medium">Privacy Policy</span>
                    <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50">
                    <span className="text-gray-700 font-medium">Help & Support</span>
                    <ChevronRight size={20} className="text-gray-400" />
                </button>
                <button
                    onClick={() => navigate('/about-us')}
                    className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                    <div className="flex items-center">
                        <Info size={20} className="text-blue-500 mr-3" />
                        <span className="text-gray-700 font-medium">About Us</span>
                    </div>
                    <ChevronRight size={20} className="text-gray-400" />
                </button>
            </div>
        </div>
    );
};

export default Profile;
